/**
 * Dates, rendered in the operator's day.
 *
 * Everything is stored and transmitted as UTC ISO strings. Everything is READ
 * in the SIGNED-IN OPERATOR'S zone — not the browser's, and no longer always
 * Asia/Kolkata. An agent in the UAE works Indian customers, and a follow-up at
 * noon IST reads 10:30 to them; showing everyone Indian hours makes the person
 * who has to place the call do the arithmetic every time.
 *
 * NOT THE HOST'S ZONE. A machine left on UTC would file a 02:00 IST follow-up
 * under yesterday for the first five and a half hours of every working day,
 * and a laptop taken on holiday would silently re-file the whole diary. The
 * zone is a setting on the account, so the same record reads the same way on
 * that person's laptop, phone and screenshots.
 */
import { DEFAULT_TIMEZONE, DEFAULT_TIMEZONE_SHORT } from '@crm/contracts';

/**
 * Module state rather than a parameter on sixty call sites.
 *
 * Set once from `/api/me` before the signed-in tree renders, and again if the
 * operator changes it. Everything here reads it through `fmts`, which is
 * rebuilt on change — an `Intl.DateTimeFormat` caches its zone at
 * construction, so the formatters cannot simply be left standing.
 */
let displayTimezone: string = DEFAULT_TIMEZONE;

/**
 * How this app writes a time, in one place.
 *
 * Shared by the two formatters below so a time in a dropdown and the same
 * time on a row can never drift into different styles — which is the whole
 * reason the clock formatter is derived from these rather than written out
 * again beside the control that needed it.
 */
const CLOCK_STYLE = { hour: 'numeric', minute: '2-digit', hour12: true } as const;

const buildFormatters = (timeZone: string) => ({
  date: new Intl.DateTimeFormat('en-IN', { timeZone, day: 'numeric', month: 'short', year: 'numeric' }),
  day: new Intl.DateTimeFormat('en-IN', { timeZone, weekday: 'short', day: 'numeric', month: 'short' }),
  time: new Intl.DateTimeFormat('en-IN', { timeZone, ...CLOCK_STYLE }),
  key: new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }),
});

let fmts = buildFormatters(displayTimezone);

/** The zone every reading on screen is in. */
export const getDisplayTimezone = (): string => displayTimezone;

/**
 * The same instant on the BUSINESS's clock — or null when that is the clock
 * the reader is already on.
 *
 * WHY THIS HAS TO EXIST. A follow-up is agreed with an Indian customer in
 * Indian time, and the schedule dialog enters it that way whoever is typing.
 * The calendar then renders in the READER's zone, so an agent in Dubai sees
 * 10:30 for a call the customer was promised at 12:00. Both are the same
 * moment and only one of them is the thing to say on the phone.
 *
 * Null rather than a duplicate when the two agree: for the half of the team
 * sitting in India, "12:00 pm · 12:00 pm IST" is noise on every row.
 */
const businessFmt = new Intl.DateTimeFormat('en-IN', { timeZone: DEFAULT_TIMEZONE, ...CLOCK_STYLE });

export const businessTime = (iso: string): string | null =>
  displayTimezone === DEFAULT_TIMEZONE ? null : `${businessFmt.format(new Date(iso))} ${DEFAULT_TIMEZONE_SHORT}`;

/**
 * Point every formatter at this operator's zone.
 *
 * Idempotent and cheap, so it is safe to call on every render of the
 * signed-in tree — which is how it stays correct without a context and a
 * re-render of everything that shows a date.
 */
export function setDisplayTimezone(timeZone: string): void {
  if (timeZone === displayTimezone) return;
  displayTimezone = timeZone;
  fmts = buildFormatters(timeZone);
}

/**
 * The same reading for a WALL CLOCK — an hour and a minute with no date and
 * no instant behind them.
 *
 * `timeZone: 'UTC'` is not a timezone decision, it is the absence of one:
 * "09:45" in a picker means nine forty-five, not an instant to be shifted
 * into Asia/Kolkata. Formatting it through `timeFmt` would add five and a
 * half hours and offer the operator 3:15 pm.
 */
const wallClockFmt = new Intl.DateTimeFormat('en-IN', { timeZone: 'UTC', ...CLOCK_STYLE });

/**
 * '13:30' -> '1:30 pm'.
 *
 * The team reads the clock in am/pm, and a picker of ninety-six rows of
 * 24-hour times is ninety-six rows they have to translate — at 3pm, under
 * the customer's attention, fifty times a day. The VALUE stays 'HH:MM',
 * because that is what the form, the date arithmetic and the best-hour
 * default all speak; only the label changes.
 *
 * Returns the input unchanged if it is not a time, rather than throwing:
 * a label is not worth taking a dialog down for.
 */
export const formatClock = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  if (!Number.isInteger(h) || !Number.isInteger(m)) return hhmm;
  return wallClockFmt.format(new Date(Date.UTC(2000, 0, 1, h, m)));
};

export const formatDate = (iso: string): string => fmts.date.format(new Date(iso));
export const formatTime = (iso: string): string => fmts.time.format(new Date(iso));
export const formatDateTime = (iso: string): string => `${fmts.date.format(new Date(iso))}, ${formatTime(iso)}`;

/** The calendar day an instant falls on, as a sortable, comparable string. */
export const dayKey = (iso: string): string => fmts.key.format(new Date(iso));

/** Today in the app timezone, in the same 'YYYY-MM-DD' form as `dayKey`. */
export const todayKey = (now: Date = new Date()): string => fmts.key.format(now);

function shiftedKey(days: number, now: Date): string {
  return fmts.key.format(new Date(now.getTime() + days * 86_400_000));
}

/**
 * A day heading a person can scan. "Today" and "Yesterday" carry more than a
 * date does, and the weekday matters when reading back a week of calls.
 */
export function formatDayHeading(iso: string, now: Date = new Date()): string {
  const key = dayKey(iso);
  if (key === todayKey(now)) return 'Today';
  if (key === shiftedKey(-1, now)) return 'Yesterday';
  if (key === shiftedKey(1, now)) return 'Tomorrow';
  return fmts.day.format(new Date(iso));
}

/**
 * How long ago, in words, for entries where the exact instant is noise. Falls
 * back to a date past a week — "43 days ago" is arithmetic, not information.
 */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const diffMs = now.getTime() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60_000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.round(hours / 24);
  if (days <= 7) return `${days}d ago`;

  return formatDate(iso);
}

/**
 * How overdue, or how soon. Signed, so one helper serves both directions —
 * a follow-up list needs "3 days late" and "in 2 days" side by side.
 */
export function formatDueDistance(iso: string, now: Date = new Date()): string {
  const diffMs = new Date(iso).getTime() - now.getTime();
  const late = diffMs < 0;
  const minutes = Math.round(Math.abs(diffMs) / 60_000);

  const span =
    minutes < 60
      ? `${Math.max(minutes, 1)}m`
      : minutes < 1440
        ? `${Math.round(minutes / 60)}h`
        : `${Math.round(minutes / 1440)}d`;

  return late ? `${span} late` : `in ${span}`;
}

/**
 * Groups entries into day buckets, newest day first, preserving the order
 * within each day. §10's timeline and the notes list both need exactly this,
 * which is why it is not written twice.
 */
export function groupByDay<T>(items: readonly T[], instantOf: (item: T) => string): { key: string; items: T[] }[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = dayKey(instantOf(item));
    const bucket = groups.get(key);
    if (bucket) bucket.push(item);
    else groups.set(key, [item]);
  }
  return [...groups.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([key, grouped]) => ({ key, items: grouped }));
}
